import dayjs from "dayjs";
import { toast } from "react-toastify";
import { LayoutTemplate } from "../LayoutTemplate";
import { MesesDoAnoEnum } from "../../../enums/mesesEnum";
import { formatDate } from "../../../utils/dateTransform";
import { maskMoney } from "../../../utils/masks";
import { card, num } from "../GeneralTemplate/ui";
import { useBankReconciliationReport } from "./useBankReconciliationReport";

const fieldLabel =
  "text-xs font-semibold uppercase tracking-wider text-[#6B6B66]";
const selectCls =
  "h-11 w-full appearance-none rounded-[10px] border border-[#DAD9D4] bg-white pl-3.5 pr-9 text-sm";
const ATALHOS = [
  { label: "Mês atual", date: () => dayjs() },
  { label: "Mês anterior", date: () => dayjs().subtract(1, "month") },
];

// Normaliza os status do Mapa e da Iugu (PT ou EN) para comparar os dois
const TONES = {
  cancelado: "bg-[#FDEDE6] text-[#B83A12]",
  expirado: "bg-[#F1F0EC] text-[#4A4A46]",
  pendente: "bg-[#FFF4D6] text-[#7A5200]",
  pago: "bg-[#E6F4EA] text-[#1E6B33]",
};
type Tone = keyof typeof TONES;
const toneOf = (status?: string): Tone | undefined => {
  const s = (status || "").toLowerCase();
  if (s.includes("cancel")) return "cancelado";
  if (s.includes("expir")) return "expirado";
  if (s.includes("pend")) return "pendente";
  if (s.includes("pag") || s.includes("paid")) return "pago";
};

const StatusPill = ({ status }: { status?: string }) => {
  const tone = toneOf(status);
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${
        tone ? TONES[tone] : "bg-[#F1F0EC] text-[#4A4A46]"
      }`}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {status || "---"}
    </span>
  );
};

const Icon = ({ d, size = 16 }: { d: string; size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d={d} />
  </svg>
);

const Chevron = () => (
  <span className="pointer-events-none absolute right-3 top-3.5 text-[#6B6B66]">
    <Icon d="M6 9l6 6 6-6" />
  </span>
);

const pageBtn =
  "inline-flex h-11 min-w-[44px] items-center justify-center rounded-[10px] border px-2 text-sm tabular-nums";

export const BankReconciliationReportTemplate = () => {
  const {
    empresa,
    pagination,
    setNumberPage,
    faturasRelacionadas,
    filter,
    appliedFilter,
    setFilter,
    yearOptions,
    mothOptions,
    getDoc,
    handleFilter,
  } = useBankReconciliationReport();

  const current = pagination.actualPage || 0;
  const totalPage = pagination.totalPage || 0;
  const total = pagination.totalRegister || 0;
  const start = Math.max(0, Math.min(current - 2, totalPage - 5));
  const pages = Array.from(
    { length: Math.min(5, totalPage) },
    (_, i) => start + i
  );
  const showLast = totalPage > 0 && !pages.includes(totalPage - 1);
  const firstItem = current * 10 + 1;
  const lastItem = Math.min(total, (current + 1) * 10);

  const copyId = (id: string) =>
    navigator.clipboard
      .writeText(id)
      .then(() => toast.success("ID da fatura copiado"));

  return (
    <LayoutTemplate title="Conciliação Bancária">
      <div className="flex flex-col gap-5 text-[#1D1D1B]">
        <span className="text-sm text-[#6B6B66]">
          {MesesDoAnoEnum[appliedFilter.mes]} de {appliedFilter.ano} ·{" "}
          {empresa}
        </span>

        {/* Filtros */}
        <form
          onSubmit={handleFilter}
          className={`${card} flex flex-wrap items-end gap-5 py-4`}
        >
          <div className="flex flex-col gap-1.5">
            <label htmlFor="conc-mes" className={fieldLabel}>
              Mês
            </label>
            <div className="relative w-[180px]">
              <select
                id="conc-mes"
                className={selectCls}
                value={filter.mes}
                onChange={(e) =>
                  setFilter((prev) => ({ ...prev, mes: +e.target.value }))
                }
              >
                {mothOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <Chevron />
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="conc-ano" className={fieldLabel}>
              Ano
            </label>
            <div className="relative w-[120px]">
              <select
                id="conc-ano"
                className={selectCls}
                value={filter.ano}
                onChange={(e) =>
                  setFilter((prev) => ({ ...prev, ano: +e.target.value }))
                }
              >
                {yearOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <Chevron />
            </div>
          </div>

          <div className="flex flex-1 flex-col gap-1.5">
            <span className={fieldLabel}>Atalhos</span>
            <div className="flex h-11 gap-0.5 self-start rounded-[10px] bg-[#F1F0EC] p-1">
              {ATALHOS.map((a) => {
                const date = a.date();
                const mes = date.month() + 1;
                const ano = date.year();
                const active = filter.mes === mes && filter.ano === ano;
                return (
                  <button
                    key={a.label}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setFilter({ mes, ano })}
                    className={`rounded-md px-3 text-[13px] ${
                      active
                        ? "bg-white font-semibold shadow-sm"
                        : "font-medium text-[#4A4A46]"
                    }`}
                  >
                    {a.label}
                  </button>
                );
              })}
            </div>
          </div>

          <button
            type="submit"
            className="h-11 rounded-[10px] bg-[#E84E1B] px-6 text-sm font-semibold text-white hover:bg-[#cf4316]"
          >
            Aplicar filtros
          </button>
        </form>

        {/* Tabela */}
        <section className="mb-8 flex flex-col overflow-hidden rounded-2xl border border-[#E7E6E2] bg-white">
          <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
            <div className="flex items-baseline gap-2.5">
              <h2 className="text-base font-semibold">Faturas do mês</h2>
              <span className="text-[13px] tabular-nums text-[#6B6B66]">
                {num(total)} faturas
              </span>
            </div>
            <button
              type="button"
              onClick={getDoc}
              className="inline-flex h-11 items-center gap-2 rounded-[10px] border border-[#DAD9D4] bg-white px-[18px] text-sm font-semibold hover:bg-[#FAFAF8]"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <path d="M7 10l5 5 5-5" />
                <path d="M12 15V3" />
              </svg>
              Exportar XLSX
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[1100px] border-collapse text-sm [&_td]:whitespace-nowrap [&_td]:px-3.5 [&_td]:py-3 [&_th]:whitespace-nowrap [&_th]:px-3.5 [&_th]:py-3">
              <thead>
                <tr className="bg-[#F7F7F5] text-left text-xs uppercase tracking-wider text-[#6B6B66] [&_th]:font-semibold">
                  <th>Fatura</th>
                  <th>Cliente</th>
                  <th>Placa/Chassi</th>
                  <th className="text-right">Criação</th>
                  <th className="text-right">Pagamento</th>
                  <th className="text-right">Valor</th>
                  <th>Status Mapa</th>
                  <th>Status Iugu</th>
                  <th>Conciliação</th>
                </tr>
              </thead>
              <tbody>
                {faturasRelacionadas?.map((i) => {
                  const mapa = toneOf(i?.statusMapa);
                  const iugu = toneOf(i?.statusIugu);
                  // Só compara quando os dois status são reconhecidos
                  const conciliado = mapa && iugu ? mapa === iugu : undefined;
                  return (
                    <tr
                      key={i?.id}
                      className={`border-t border-[#EEEDE9] ${
                        conciliado === false
                          ? "bg-[#FFF8F5]"
                          : "hover:bg-[#FAFAF8]"
                      }`}
                    >
                      <td>
                        <span className="inline-flex items-center gap-1.5">
                          <span
                            title={i?.id}
                            className="font-mono text-[13px] text-[#4A4A46]"
                          >
                            {i?.id?.length > 14
                              ? `${i.id.slice(0, 8)}…${i.id.slice(-4)}`
                              : i?.id}
                          </span>
                          <button
                            type="button"
                            aria-label="Copiar ID da fatura"
                            onClick={() => copyId(i?.id)}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-[#6B6B66] hover:bg-[#F1F0EC]"
                          >
                            <Icon
                              size={15}
                              d="M9 9h10a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-8a2 2 0 0 1-2-2zM5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"
                            />
                          </button>
                        </span>
                      </td>
                      <td className="font-semibold">
                        {i?.clienteNome || "---"}
                      </td>
                      <td className="font-mono text-[13px]">
                        {i?.placaChassi || "---"}
                      </td>
                      <td className="text-right tabular-nums text-[#4A4A46]">
                        {formatDate(i?.dataCriacao) || "—"}
                      </td>
                      <td className="text-right tabular-nums text-[#4A4A46]">
                        {formatDate(i?.dataPagamento) || "—"}
                      </td>
                      <td className="text-right font-semibold tabular-nums">
                        {maskMoney(i?.valorTotal)}
                      </td>
                      <td>
                        <StatusPill status={i?.statusMapa} />
                      </td>
                      <td>
                        <StatusPill status={i?.statusIugu} />
                      </td>
                      <td>
                        {conciliado === undefined ? (
                          <span className="text-[#6B6B66]">—</span>
                        ) : (
                          <span
                            className={`inline-flex items-center gap-1.5 text-[13px] font-semibold ${
                              conciliado ? "text-[#1E6B33]" : "text-[#B83A12]"
                            }`}
                          >
                            <Icon
                              d={
                                conciliado
                                  ? "M20 6L9 17l-5-5"
                                  : "M12 9v4M12 17h.01M10.3 3.9L1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"
                              }
                            />
                            {conciliado ? "Conciliado" : "Divergente"}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {!faturasRelacionadas?.length && (
                  <tr className="border-t border-[#EEEDE9]">
                    <td colSpan={9} className="py-10 text-center text-[#6B6B66]">
                      Nenhuma fatura encontrada no período.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {totalPage > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[#EEEDE9] px-5 py-3">
              <span className="text-[13px] tabular-nums text-[#6B6B66]">
                Mostrando{" "}
                <b className="font-semibold text-[#1D1D1B]">
                  {firstItem}–{lastItem}
                </b>{" "}
                de <b className="font-semibold text-[#1D1D1B]">{num(total)}</b>
              </span>
              <nav aria-label="Paginação" className="flex items-center gap-1">
                <button
                  type="button"
                  aria-label="Página anterior"
                  disabled={current === 0}
                  onClick={() => setNumberPage(current - 1)}
                  className={`${pageBtn} border-[#DAD9D4] bg-white disabled:border-[#E7E6E2] disabled:text-[#BDBCB6]`}
                >
                  <Icon d="M15 18l-6-6 6-6" />
                </button>
                {pages.map((p) => (
                  <button
                    key={p}
                    type="button"
                    aria-current={p === current ? "page" : undefined}
                    onClick={() => setNumberPage(p)}
                    className={`${pageBtn} ${
                      p === current
                        ? "border-[#1D1D1B] bg-[#1D1D1B] font-semibold text-white"
                        : "border-[#DAD9D4] bg-white font-medium"
                    }`}
                  >
                    {p + 1}
                  </button>
                ))}
                {showLast && (
                  <>
                    <span className="px-1 text-[#6B6B66]">…</span>
                    <button
                      type="button"
                      onClick={() => setNumberPage(totalPage - 1)}
                      className={`${pageBtn} border-[#DAD9D4] bg-white font-medium`}
                    >
                      {totalPage}
                    </button>
                  </>
                )}
                <button
                  type="button"
                  aria-label="Próxima página"
                  disabled={current >= totalPage - 1}
                  onClick={() => setNumberPage(current + 1)}
                  className={`${pageBtn} border-[#DAD9D4] bg-white disabled:border-[#E7E6E2] disabled:text-[#BDBCB6]`}
                >
                  <Icon d="M9 18l6-6-6-6" />
                </button>
              </nav>
            </div>
          )}
        </section>
      </div>
    </LayoutTemplate>
  );
};
